/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';

import {
  useFormFieldGroup,
  useInlineFieldLabel,
} from '../_common/form-control';
import { type CommonProps, type HtmlAttributes } from '../_common/types';
import { useSemiControlledInput } from '../_common/use-semi-controlled-input';

type RadioProps = {
  /** Whether the radio is checked. Ignored when a parent `RadioGroup` has a `value` */
  isChecked?: boolean;
  /** Selects the radio by default in uncontrolled mode */
  isDefaultChecked?: boolean;
  /** Whether the radio is disabled */
  isDisabled?: boolean;
  /** Whether the radio & label should take the full available width of its container */
  isFluid?: boolean;
  /** Whether the label should be displayed before the radio */
  hasLeadingLabel?: boolean;
  /** The callback function triggered when the radio value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** The value of the radio */
  value?: HtmlAttributes<'input'>['value'];
  /** The callback function triggered when the radio is clicked */
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
} & (
  | {
      /** The aria label of the radio. Required if the label is not a string. */
      ariaLabel: string;
      // TODO v5: inconsistent naming. Naming the radio takes a union of `label`
      // and `ariaLabel`, so the accessible name comes from a different prop
      // depending on what the label renders. Combobox instead requires the
      // label and clips it with `hasVisuallyHiddenLabel`, and TextInput reaches
      // for htmlAttributes['aria-label'].
      /** The label of the radio. If not a string, supply an aria label for screen reader support. */
      label?: undefined;
    }
  | {
      ariaLabel?: string;
      label: React.ReactNode;
    }
);

export const Radio = ({
  ariaLabel,
  isDisabled = false,
  isDefaultChecked,
  label,
  isFluid = false,
  isChecked,
  hasLeadingLabel,
  onChange,
  className,
  style,
  onClick,
  value,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'input', RadioProps>) => {
  const group = useFormFieldGroup('radio');
  const { 'aria-describedby': htmlAriaDescribedBy, ...inputHtmlAttributes } =
    htmlAttributes ?? {};
  const isDisabledState = isDisabled || group?.isDisabled === true;
  const isGroupControlled = group?.value !== undefined;
  const isUsingNativeGroupState =
    group !== undefined && !isGroupControlled && isChecked === undefined;
  const [isCheckedState, internalOnChange] = useSemiControlledInput({
    inputType: 'radio',
    isControlled: isChecked !== undefined,
    onChange,
    state: isChecked ?? isDefaultChecked ?? false,
  });
  const isResolvedChecked = isGroupControlled
    ? value !== undefined && String(value) === group?.value
    : isCheckedState;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    internalOnChange(event);
    group?.onChange?.(event);
  };

  const radioClasses = classNames('ndl-radio', className, {
    'ndl-disabled': isDisabledState,
  });

  const { labelContent, fieldClassName } = useInlineFieldLabel({
    ariaLabel,
    componentName: 'Radio',
    isDisabled: isDisabledState,
    isFluid,
    // TODO v5: decide whether all selection controls should truncate; today
    // only Radio does.
    isTruncated: true,
    label,
  });

  return (
    <label className={fieldClassName}>
      {hasLeadingLabel === true && labelContent}
      <input
        ref={ref}
        type="radio"
        aria-label={ariaLabel}
        aria-describedby={htmlAriaDescribedBy}
        disabled={isDisabledState}
        checked={isUsingNativeGroupState ? undefined : isResolvedChecked}
        defaultChecked={isUsingNativeGroupState ? isDefaultChecked : undefined}
        name={group?.name}
        required={group?.isRequired || undefined}
        onClick={onClick}
        onChange={handleChange}
        value={value}
        style={style}
        className={radioClasses}
        {...restProps}
        {...inputHtmlAttributes}
      />
      {Boolean(hasLeadingLabel) === false && labelContent}
    </label>
  );
};
